<p-confirm-dialog [draggable]="false" />

@if (loadingForm) {
<div class="tw-flex tw-justify-center tw-py-6">
    <p-progress-spinner styleClass="p-progress-spinner-lg" strokeWidth="4" />
</div>
} @else {
<mon-page-layout
    [header]="(isEditMode ? 'admin.user.form.header.edit' : 'admin.user.form.header.add') | transloco"
    [formLayout]="'single-column'"
>
    <form [formGroup]="userForm" (ngSubmit)="onSubmit()" class="admin-user-form" data-test="admin-user-form">
        <div class="tw-mb-6">
            <p-card [styleClass]="'op-form-card'">
                <div class="tw-mb-6">
                    <h1 class="op-card-header">
                        {{ 'admin.user.form.detailsCard.header' | transloco }}
                    </h1>
                </div>
                <div>
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
                        <label for="firstName">{{ 'admin.user.form.detailsCard.firstNameLabel' | transloco }}*</label>
                        <input pInputText
                            id="firstName"
                            [placeholder]="'admin.user.form.detailsCard.firstNamePlaceholder' | transloco"
                            formControlName="firstName"
                            data-test="admin-user-form-first-name-input"
                            [required]="true" />
                        @if (isFieldInvalid('firstName')) {
                            <p-message severity="error" variant="simple" size="small">
                                {{ 'admin.user.form.detailsCard.firstName.required' | transloco }}
                            </p-message>
                        }
                    </div>
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
                        <label for="lastName">{{ 'admin.user.form.detailsCard.lastNameLabel' | transloco }}</label>
                        <input pInputText
                            id="lastName"
                            [placeholder]="'admin.user.form.detailsCard.lastNamePlaceholder' | transloco"
                            formControlName="lastName"
                            data-test="admin-user-form-last-name-input" />
                    </div>
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
                        <label for="email">{{ 'admin.user.form.detailsCard.emailLabel' | transloco }}*</label>
                        <input pInputText
                            id="email"
                            [placeholder]="'admin.user.form.detailsCard.emailPlaceholder' | transloco"
                            formControlName="email"
                            data-test="admin-user-form-email-input"
                            [required]="true" />
                        @if (isFieldInvalid('email')) {
                            <p-message severity="error" variant="simple" size="small">
                                @if (getFieldError('email', 'required')) {
                                    {{ 'admin.user.form.detailsCard.email.required' | transloco }}
                                }
                                @if (getFieldError('email', 'email')) {
                                    {{ 'admin.user.form.detailsCard.email.invalid' | transloco }}
                                }
                            </p-message>
                        }
                    </div>
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-6">
                        <label for="interfaceLanguage">{{ 'admin.user.form.detailsCard.interfaceLanguageLabel' | transloco }}</label>
                        <p-select
                            id="interfaceLanguage"
                            [options]="languages"
                            formControlName="interfaceLanguage"
                            optionLabel="name"
                            optionValue="value"
                            [filter]="true"
                            filterBy="name"
                            data-test="admin-user-form-interface-language-select"
                            [placeholder]="'admin.user.form.detailsCard.interfaceLanguagePlaceholder' | transloco"
                        />
                    </div>

                    <div class="tw-mb-4">
                        <p-checkbox inputId="isAdmin" formControlName="isAdmin" [binary]="true" data-test="admin-user-form-is-admin-checkbox" (onChange)="onAdminStatusChange()" />
                        <label for="isAdmin" class="tw-ml-2">{{ 'admin.user.form.detailsCard.isAdminLabel' | transloco }}</label>
                    </div>
                    @if (!isEditMode) {
                        <div class="tw-mb-4">
                            <p-checkbox inputId="sendLoginInstructions" formControlName="sendLoginInstructions" [binary]="true" data-test="admin-user-form-send-login-instructions-checkbox" />
                            <label for="sendLoginInstructions" class="tw-ml-2">{{ 'admin.user.form.detailsCard.sendLoginInstructionsLabel' | transloco }}</label>
                        </div>
                    }
                    <div class="tw-mb-4">
                        <p-checkbox inputId="notifyOnExport" formControlName="notifyOnExport" [binary]="true" data-test="admin-user-form-notify-on-export-checkbox" />
                        <label for="notifyOnExport" class="tw-ml-2">{{ 'admin.user.form.detailsCard.notifyOnExportLabel' | transloco }}</label>
                    </div>
                </div>
            </p-card>
        </div>

        <div class="tw-mb-6">
            <p-card>
                <div class="tw-mb-6">
                    <h1 class="op-card-header">
                        {{ 'admin.user.form.permissionsCard.header' | transloco }}
                    </h1>
                    <h2 class="op-card-sub-header">{{'Select which features this user can access'}}</h2>
                </div>
                <div formGroupName="permissions">
                    <div class="tw-mb-4">
                        <p-checkbox inputId="allFeatures" formControlName="allFeatures" [binary]="true" data-test="admin-user-form-all-features-checkbox" (onChange)="toggleAllPermissions($event)" />
                        <label for="allFeatures" class="tw-ml-2">{{ 'admin.user.form.permissionsCard.allFeaturesLabel' | transloco }}</label>
                    </div>
                    <hr />
                    @for (permission of permissions; track permission.key) {
                        <div class="tw-mb-4">
                            <p-checkbox [inputId]="permission.key" [formControlName]="permission.key" [binary]="true" [attr.data-test]="'admin-user-form-permission-' + permission.key + '-checkbox'" (onChange)="updateAllFeaturesCheckbox()" />
                            <label for="{{ permission.key }}" class="tw-ml-2">{{ permission.label }}</label>
                        </div>
                    }
                </div>
            </p-card>
        </div>

        <div>
            <p-card>
                <div class="tw-mb-6">
                    <h1 class="op-card-header">
                        {{ 'admin.user.form.domainsCard.header' | transloco }}
                    </h1>
                    <h2 class="op-card-sub-header">{{'Select which domains this user can access'}}</h2>
                </div>
                <p-table
                    [value]="currentPageDomains"
                    [responsiveLayout]="'scroll'"
                    [scrollable]="true"
                    scrollHeight="flex"
                    styleClass="tw-flex-grow"
                    tableStyleClass="tw-table-fixed"
                    data-test="admin-user-form-domains-table"
                    [loading]="loadingDomains"
                    [paginator]="true"
                    [paginatorDropdownAppendTo]="'body'"
                    [lazy]="true"
                    [rows]="perPage"
                    [totalRecords]="total"
                    [first]="tableFirstOffset"
                    [rowsPerPageOptions]="AVAILABLE_PAGE_SIZE"
                    (onPage)="onPageChange($event)"
                >
                    <caption class="sr-only">{{ 'admin.user.form.domains.table.caption' | transloco }}</caption>
                    <ng-template #header>
                        <tr>
                            <th scope="col" data-test="admin-user-form-domain-tableHead">{{ 'admin.user.form.domainsCard.table.domain' | transloco }}</th>
                            <th scope="col" data-test="admin-user-form-visible-tableHead">
                                <p-checkbox
                                    inputId="allVisible"
                                    formControlName="allVisible"
                                    [binary]="true"
                                    [indeterminate]="allVisibleIndeterminate"
                                    [attr.aria-label]="'admin.user.form.domainsCard.table.selectAllVisible' | transloco"
                                    (onChange)="onToggleAllVisible($event)"
                                />
                                <label for="allVisible" class="tw-ml-2">{{ 'admin.user.form.domainsCard.table.visibleLabel' | transloco }}</label>
                            </th>
                            <th scope="col" data-test="admin-user-form-sendReport-tableHead">
                                <p-checkbox
                                    inputId="allSendReport"
                                    formControlName="allSendReport"
                                    [binary]="true"
                                    [indeterminate]="allSendReportIndeterminate"
                                    [attr.aria-label]="'admin.user.form.domainsCard.table.selectAllSendReport' | transloco"
                                    (onChange)="onToggleAllSendReport($event)"
                                />
                                <label for="allSendReport" class="tw-ml-2">{{ 'admin.user.form.domainsCard.table.sendReportLabel' | transloco }}</label>
                            </th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-domain let-i="rowIndex">
                        @let domainFG = currentPageDomainFormControlByDomainId[domain.id];
                        <tr [formGroup]="domainFG">
                            <td data-test="admin-user-form-domain-tableCell">{{ domainFG.get('title')?.value }}</td>
                            <td data-test="admin-user-form-visible-tableCell">
                                <p-checkbox inputId="domainVisible{{ i }}"
                                    formControlName="visible"
                                    [binary]="true"
                                    [attr.aria-label]="('admin.user.form.domainsCard.table.visibleLabel' | transloco) + ' ' + (domainFG.get('title')?.value)"
                                    (onChange)="onDomainVisibleChange(domainFG, $event)"
                                />
                            </td>
                            <td data-test="admin-user-form-sendReport-tableCell">
                                <p-checkbox inputId="domainSendReport{{ i }}"
                                    formControlName="sendReport"
                                    [binary]="true"
                                    [attr.aria-label]="('admin.user.form.domainsCard.table.sendReportLabel' | transloco) + ' ' + (domainFG.get('title')?.value)"
                                    (onChange)="updateHeaderCheckboxes()"
                                />
                            </td>
                        </tr>
                        @let domainGroups = currentPageDomainGroupsByDomainId[domain.id];
                        @for (domainGroup of domainGroups; track domainGroup.id) {
                            @let domainGroupFG = currentPageDomainGroupFormControlByDomainGroupId[domainGroup.id];
                             <tr [formGroup]="domainGroupFG">
                                <td data-test="admin-user-form-domain-tableCell"><i class="pi pi-clone tw-mr-1"></i>{{ domainGroupFG.get('title')?.value }}</td>
                                <td data-test="admin-user-form-visible-tableCell">
                                    <p-checkbox inputId="domainVisible{{ i }}"
                                        formControlName="visible"
                                        [binary]="true"
                                        [attr.aria-label]="('admin.user.form.domainsCard.table.visibleLabel' | transloco) + ' ' + (domainGroupFG.get('title')?.value)"
                                        (onChange)="onDomainVisibleChange(domainGroupFG, $event)"
                                    />
                                </td>
                                <td data-test="admin-user-form-sendReport-tableCell">
                                    <p-checkbox inputId="domainSendReport{{ i }}"
                                        formControlName="sendReport"
                                        [binary]="true"
                                        [attr.aria-label]="('admin.user.form.domainsCard.table.sendReportLabel' | transloco) + ' ' + (domainGroupFG.get('title')?.value)"
                                        (onChange)="updateHeaderCheckboxes()"
                                    />
                                </td>
                            </tr>
                        }
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td colspan="3" data-test="admin-users-empty-tableCell">{{ 'admin.user.form.domains.table.emptyMessage' | transloco }}</td>
                        </tr>
                    </ng-template>
                </p-table>
            </p-card>
        </div>

        <div class="form-actions">
            <p-button
                data-test="admin-user-form-cancel-button"
                [label]="'admin.user.form.cancelButtonLabel' | transloco"
                variant="text"
                [disabled]="savingForm"
                (onClick)="onCancel()"
            />
            <p-button
                data-test="admin-user-form-save-button"
                type="submit"
                [label]="'admin.user.form.saveButtonLabel' | transloco"
                [loading]="savingForm"
                styleClass="tw-ml-4"
                [disabled]="userForm.invalid"
            />
        </div>
    </form>
</mon-page-layout>
}
